import 'dayjs/locale/es';

import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { UsersIcon } from '../assets';
import { Breadcrumbs } from '../components/common/Breadcrumbs';
import { TableSearchInput } from '../components/inputs/TableSearchInput';
import { CreateInfluentiaUser } from '../components/popups/CreateInfluentiaUser';
import DataTable from '../components/tables/DataTable';
import { getInfluentiaUsersTableHeaders } from '../components/tables/headers/influentia-users';
import { ROLES } from '../config/constants';
import { replaceIds, ROUTES } from '../config/routes/config';
import { GET_USERS } from '../graphql/user/query';
import { useTableDataAndFilters } from '../hooks/useTableDataAndFilters';
import { influentiaUsersFilterAtom } from '../states/atoms';
import type {
  InfluentiaUsersFilters,
  InfluentiaUsersTable,
  ResponseDataUsersList,
} from '../types';

dayjs.extend(localizedFormat);
dayjs.locale('es');

function InfluentiaUsers() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  /**
   * Hook in charge of the logic of the data, filtering and pagination
   */
  const {
    resetPagination,
    total,
    filters,
    setFilters,
    table,
    error,
    loading,
    refetch,
  } = useTableDataAndFilters<
    InfluentiaUsersFilters,
    InfluentiaUsersTable,
    {},
    ResponseDataUsersList
  >({
    filterAtom: influentiaUsersFilterAtom,
    getColumns: getInfluentiaUsersTableHeaders,
    columnsExtras: {},
    responseAccessKey: 'users',
    query: GET_USERS,
    extraVariables: {
      role: ROLES.influentia,
    },
  });

  /**
   * Handles click on any row of the table and navigates to single influentia user
   * @param id id of the row clicked
   */
  const handleTableClick = (id: string) => {
    navigate(
      replaceIds({
        url: ROUTES.SINGLE_INFLUENTIA_USER,
        params: [{ id }],
      }),
    );
  };

  return (
    <div className="w-full flex flex-col gap-y-5">
      <Breadcrumbs
        icon={<UsersIcon />}
        breadcrumbs={[
          {
            text: t('influentiaUsers.title'),
          },
        ]}
      />
      <div className="flex flex-col gap-y-4">
        <div className="flex flex-row justify-between">
          <div>
            <TableSearchInput
              placeholder={t('influentiaUsers.searchInputPlaceholder')}
              filters={{ ...filters }}
              setFilters={(val) => {
                setFilters(val);
                resetPagination();
              }}
              searchKey="name"
            />
          </div>
          <div className="flex flex-row gap-x-2">
            <CreateInfluentiaUser refetch={refetch} />
          </div>
        </div>
        <DataTable
          error={error}
          handleRowClick={(id) => handleTableClick(id)}
          loading={loading}
          table={table}
          total={total}
          tableId="InfluentiaUsersTable"
          sorting={{ ...filters }}
        />
      </div>
    </div>
  );
}

export default InfluentiaUsers;
